import { useEffect, useState, useMemo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Avatar,
  Typography,
  Paper,
  Fade,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import RobotAvatar from "../../assets/images/Robot.svg";
import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import { TypingDotsComponent } from "./TypingDots";
import { MessageContent } from "./MessageContent";
import {
  StyledPaper,
  MessageContainer,
  CssTextField,
  MessageBubble,
  Message,
  InputContainer,
} from "./ChatStyles";
import { ColorsContext } from "../../Context/ColorsContext";

export const ChatUI = ({
  messages = [],
  onSendMessage,
  isTyping,
  isSending = false,
  chatEndRef,
}) => {
  const { colors } = useContext(ColorsContext);
  const [newMessage, setNewMessage] = useState("");
  const [currentAnimatedMessageId, setCurrentAnimatedMessageId] =
    useState(null);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      chatEndRef?.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, [chatEndRef]);

  useEffect(() => {
    if (!isSmallScreen) {
      scrollToBottom();
    }
  }, [isSmallScreen, scrollToBottom, isTyping, messages]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage.sent && lastMessage.text?.trim()) {
        setCurrentAnimatedMessageId(lastMessage.id);
      } else {
        setCurrentAnimatedMessageId(null);
      }
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (isSending) {
      return;
    }

    if (newMessage.trim()) {
      const message = {
        created_at: new Date().toISOString(),
        id: `user-${Date.now()}-${Math.random()}`,
        sent: true,
        text: newMessage.trim(),
      };
      onSendMessage(message);
      setNewMessage("");
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return new Intl.DateTimeFormat("es-ES", options).format(date);
  };

  const renderedMessages = useMemo(
    () =>
      messages
        .filter((message) => message.text?.trim())
        .map((message) => {
          return (
            <Fade in={true} key={message.id} timeout={500}>
              <MessageBubble sent={message?.sent} id={message.id}>
                <Avatar
                  sx={{
                    backgroundColor: message?.sent
                      ? "#bdbdbd"
                        : colors.navbarColor,
                  }}
                >
                  {message?.sent ? (
                    <PersonIcon />
                  ) : (
                    <Box
                      component="img"
                      src={RobotAvatar}
                      alt="Assistant"
                      sx={{ width: 30, height: 30 }}
                    />
                  )}
                </Avatar>
                <Message
                  sent={message?.sent}
                  bubbleColor={colors.main}
                >
                  <MessageContent
                    message={message}
                    currentAnimatedMessageId={currentAnimatedMessageId}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={
                        message?.sent
                          ? "rgba(17,24,39,0.7)"
                          : "text.secondary"
                      }
                      sx={{
                        textAlign: message?.sent ? "right" : "left",
                        display: "block",
                        width: "100%",
                        marginTop: message?.sent ? 0 : 0.75,
                        fontSize: message?.sent ? "0.78rem" : "0.75rem",
                      }}
                    >
                      {formatTime(message?.created_at)}
                    </Typography>
                  </Box>
                </Message>
              </MessageBubble>
            </Fade>
          );
        }),
    [messages, currentAnimatedMessageId, colors.main, colors.navbarColor]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Paper sx={StyledPaper} id="chatContainer">
        <Box sx={MessageContainer}>
          {renderedMessages}
          {isTyping && (
            <MessageBubble sent={false}>
              <Avatar sx={{ backgroundColor: colors.navbarColor }}>
                <Box
                  component="img"
                  src={RobotAvatar}
                  sx={{ width: 30, height: 30 }}
                />
              </Avatar>
              <Message sent={false}>
                <TypingDotsComponent />
              </Message>
            </MessageBubble>
          )}
          <div ref={chatEndRef} />
        </Box>
        <InputContainer>
          <CssTextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Escribe una pregunta..."
            value={newMessage}
            disabled={isSending}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isSending) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            aria-label="Escribe una pregunta"
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={isSending || !newMessage.trim()}
            sx={{
              ml: 1,
              color: colors.main,
              borderRadius: "50%",
            }}
            aria-label="Send message"
          >
            <SendIcon />
          </IconButton>
        </InputContainer>
      </Paper>
    </Box>
  );
};

ChatUI.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string,
      sent: PropTypes.bool,
      created_at: PropTypes.string,
    })
  ),
  onSendMessage: PropTypes.func.isRequired,
  isTyping: PropTypes.bool.isRequired,
  isSending: PropTypes.bool,
  chatEndRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
};
